import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import SearchIcon       from '@mui/icons-material/Search';
import AutoAwesomeIcon  from '@mui/icons-material/AutoAwesome';
import Colors           from '../../../library/Colors';
import i18n             from '../../../i18n';

// ────────────────────────────────────────────────────────────────────────
// Webshop config search — easter-egg unlocked feature.
//
// Click the Shortcuts main tab 10 times to reveal the search bar. The bar
// jumps the user to any tab / sub-tab / config modal in WebshopConfigScreen.
//
// Usage in the parent screen:
//   const search = useWebshopSearch({ ...adapters });
//   <Tab label="Shortcuts" onClick={search.bumpShortcutClicks} />
//   <WebshopSearchBar search={search} />
//
// All admin labels MUST go through i18n (cfg_* keys) so the search index
// reads the same translations the tabs render — see the search-navigation
// project memory for context.
// ────────────────────────────────────────────────────────────────────────

const UNLOCK_CLICKS = 10;

// ────────────────────────────────────────────────────────────────────────
// Prefix → location map. Used to classify EVERY `cfg_*` translation key
// into a tab / sub-tab / modal destination. Order matters: most-specific
// prefixes go first so we don't accidentally route `cfg_proc_pay_*` to
// the generic `cfg_proc_*` bucket.
//
// When a new admin section gets its own `cfg_<scope>_*` prefix, add an
// entry here so search hits route to the right place.
// ────────────────────────────────────────────────────────────────────────
const PREFIX_ROUTES = [
    // ── Processing — sub-tab specific buckets first, then catch-alls ──
    { prefix: 'cfg_proc_pay_',          crumb: 'Processing › Payments',          main: 3, sub: { procTab: 1 } },
    { prefix: 'cfg_proc_ship_',         crumb: 'Processing › Shipping & pickup', main: 3, sub: { procTab: 2 } },
    { prefix: 'cfg_proc_cf_',           crumb: 'Processing › Customer fields',   main: 3, sub: { procTab: 3 } },
    { prefix: 'cfg_proc_tab_',          crumb: 'Processing',                     main: 3 },
    { prefix: 'cfg_proc_',               crumb: 'Processing › General',           main: 3, sub: { procTab: 0 } },

    // ── Sync ──
    { prefix: 'cfg_sync_imgsrc_',       crumb: 'Sync-settings › Images',         main: 4, sub: { syncTab: 2 } },
    { prefix: 'cfg_sync_imgproc_',      crumb: 'Sync-settings › Images',         main: 4, sub: { syncTab: 2 } },
    { prefix: 'cfg_sync_cat_',          crumb: 'Sync-settings › Field mapping',  main: 4, sub: { syncTab: 1 } },
    { prefix: 'cfg_sync_pfields_',      crumb: 'Sync-settings › Field mapping',  main: 4, sub: { syncTab: 1 } },
    { prefix: 'cfg_sync_lang_',         crumb: 'Sync-settings › Field mapping',  main: 4, sub: { syncTab: 1 } },
    { prefix: 'cfg_sync_tab_',          crumb: 'Sync-settings',                  main: 4 },
    { prefix: 'cfg_sync_',                crumb: 'Sync-settings › General',        main: 4, sub: { syncTab: 0 } },

    // ── General sub-tabs ──
    { prefix: 'cfg_voucher_',           crumb: 'General › Cadeaubon',            main: 0, sub: { generalTab: 0 } },
    { prefix: 'cfg_brands_',            crumb: 'General › Merken',               main: 0, sub: { generalTab: 2 } },
    { prefix: 'cfg_stl_',               crumb: 'General › Shop the look',        main: 0, sub: { generalTab: 1 } },
    { prefix: 'cfg_banners_',           crumb: 'General › Banners',              main: 0, sub: { generalTab: 3 } },
    { prefix: 'cfg_overige_',           crumb: 'General › Overige',              main: 0, sub: { generalTab: 4 } },

    // ── Pages — static page editors and their sub-modals ──
    { prefix: 'cfg_static_about_',      crumb: 'Pages › About',                  main: 1, sub: { pagesTab: 3 } },
    { prefix: 'cfg_static_disclaimer_', crumb: 'Pages › Disclaimer',             main: 1, sub: { pagesTab: 4 } },
    { prefix: 'cfg_static_privacy_',    crumb: 'Pages › Privacy',                main: 1, sub: { pagesTab: 5 } },
    { prefix: 'cfg_static_intro_',      crumb: 'Pages › Front page › Welcome',   main: 1, sub: { pagesTab: 0 } },
    { prefix: 'cfg_static_',            crumb: 'Pages › Static page',            main: 1, sub: { pagesTab: 3 } },
    { prefix: 'cfg_dyn_',               crumb: 'Pages › Dynamic page',           main: 1, sub: { pagesTab: 6 } },
    { prefix: 'cfg_carousel_',          crumb: 'Pages › Front page › Carousel',  main: 1, sub: { pagesTab: 0 } },
    { prefix: 'cfg_block_',             crumb: 'Pages › Front page',             main: 1, sub: { pagesTab: 0 } },
    { prefix: 'cfg_pages_',             crumb: 'Pages',                          main: 1 },

    // ── Style modals ──
    { prefix: 'cfg_pdi_',               crumb: 'Style › Product detail info',    main: 2, sub: { styleTab: 1 }, modal: { section: 'productDetailInfo', mode: 'config' } },
    { prefix: 'cfg_pcard_',             crumb: 'Style › Product card',           main: 2, sub: { styleTab: 0 }, modal: { section: 'productCard',       mode: 'config' } },
    { prefix: 'cfg_groups_',            crumb: 'Style › Filters & sorting',      main: 2, sub: { styleTab: 0 }, modal: { section: 'groups',            mode: 'config' } },
    { prefix: 'cfg_widths_',            crumb: 'Style › Global widths',          main: 2,                       modal: { section: 'globalWidths',      mode: 'config' } },
    { prefix: 'cfg_gallery_',           crumb: 'Style › Image gallery',          main: 2, sub: { styleTab: 1 }, modal: { section: 'image',             mode: 'config' } },
    // `cfg_extra_footer_` must be checked before `cfg_extra_` (defensive
    // ordering — `cfg_extra_` doesn't exist today but the more-specific
    // prefix should always win).
    { prefix: 'cfg_extra_footer_',      crumb: 'Style › Footer (extra)',         main: 2, sub: { styleTab: 0 }, modal: { section: 'footerExtra',       mode: 'config' } },
    { prefix: 'cfg_header_',            crumb: 'Style › Header (logo)',          main: 2, sub: { styleTab: 0 }, modal: { section: 'header',            mode: 'config' } },
    { prefix: 'cfg_footer_',            crumb: 'Style › Footer',                 main: 2, sub: { styleTab: 0 }, modal: { section: 'footer',            mode: 'config' } },
    { prefix: 'cfg_nav_',               crumb: 'Style › Menu bar',               main: 2, sub: { styleTab: 0 }, modal: { section: 'menu',              mode: 'config' } },
    { prefix: 'cfg_style_tab_',         crumb: 'Style',                          main: 2 },

    // Last-resort catch-all so any forgotten cfg_* still routes somewhere.
    { prefix: 'cfg_',                   crumb: 'Webshop',                        main: 0 },
];

// Resolve a translation key to its target location, or null if the key
// doesn't start with `cfg_`.
const classifyKey = (key) => {
    for (const r of PREFIX_ROUTES) {
        if (key.startsWith(r.prefix)) return r;
    }
    return null;
};

// Iterate over every `cfg_*` translation in the active language and turn
// each string value into a search entry. Description / help suffixes
// (`_desc`, `_help`, `_subtitle`) are kept too — they're long but contain
// keywords (e.g. "Background, text color, and size of the badge") that
// users might type. Heading / label keys without those suffixes are still
// shown front-and-centre because their values are short.
const buildFullIndex = (lang) => {
    const bundle = i18n.getResourceBundle(lang || i18n.language || 'en', 'translation') || {};
    const out = [];
    for (const [key, val] of Object.entries(bundle)) {
        if (typeof val !== 'string') continue;
        if (!key.startsWith('cfg_')) continue;
        const route = classifyKey(key);
        if (!route) continue;
        out.push({
            id:    `tx-${key}`,            // namespace so curated ids never collide
            label: val,
            crumb: route.crumb,
            keywords: key,                 // also match against the technical key
            main:  route.main,
            sub:   route.sub,
            modal: route.modal,
        });
    }
    return out;
};

// Build the navigation index. Each entry maps a search hit to one of:
//   - main tab (number) + optional sub-tab object
//   - main tab + sub-tab + modal { section, mode }
// `keywords` is a free-form string that widens matches beyond the visible
// label — typing "vouchers" should find Cadeaubon, "ratio" should find
// Product card, etc.
const buildSearchIndex = (t) => {
    const tr = (key, fb) => (t ? t(key, fb) : fb);
    return [
        // ── Main tabs ──
        { id: 'main-general',    main: 0, label: tr('cfg_main_general',    'General'),       crumb: tr('cfg_main_general',    'General'),       keywords: 'general algemeen général' },
        { id: 'main-pages',      main: 1, label: tr('cfg_main_pages',      'Pages'),         crumb: tr('cfg_main_pages',      'Pages'),         keywords: 'pages pagina pages' },
        { id: 'main-style',      main: 2, label: tr('cfg_main_style',      'Style'),         crumb: tr('cfg_main_style',      'Style'),         keywords: 'style stijl' },
        { id: 'main-processing', main: 3, label: tr('cfg_main_processing', 'Processing'),    crumb: tr('cfg_main_processing', 'Processing'),    keywords: 'processing orders' },
        { id: 'main-sync',       main: 4, label: tr('cfg_main_sync',       'Sync-settings'), crumb: tr('cfg_main_sync',       'Sync-settings'), keywords: 'sync synchronisation product data' },
        { id: 'main-shortcuts',  main: 5, label: tr('cfg_main_shortcuts',  'Shortcuts'),     crumb: tr('cfg_main_shortcuts',  'Shortcuts'),     keywords: 'shortcuts keyboard' },

        // ── General sub-tabs ──
        { id: 'gen-cadeaubon',   main: 0, sub: { generalTab: 0 }, label: tr('cfg_general_tab_cadeaubon', 'Cadeaubon'),     crumb: `${tr('cfg_main_general', 'General')} › ${tr('cfg_general_tab_cadeaubon', 'Cadeaubon')}`,     keywords: 'gift voucher cadeaubon bon cadeau cheque' },
        { id: 'gen-stl',         main: 0, sub: { generalTab: 1 }, label: tr('cfg_general_tab_stl',       'Shop the look'), crumb: `${tr('cfg_main_general', 'General')} › ${tr('cfg_general_tab_stl',       'Shop the look')}`, keywords: 'shop the look outfit lookbook' },
        { id: 'gen-merken',      main: 0, sub: { generalTab: 2 }, label: tr('cfg_general_tab_merken',    'Merken'),        crumb: `${tr('cfg_main_general', 'General')} › ${tr('cfg_general_tab_merken',    'Merken')}`,        keywords: 'brands merken marques' },
        { id: 'gen-banners',     main: 0, sub: { generalTab: 3 }, label: tr('cfg_general_tab_banners',   'Banners'),       crumb: `${tr('cfg_main_general', 'General')} › ${tr('cfg_general_tab_banners',   'Banners')}`,       keywords: 'banner banners information' },
        { id: 'gen-overige',     main: 0, sub: { generalTab: 4 }, label: tr('cfg_general_tab_overige',   'Overige'),       crumb: `${tr('cfg_main_general', 'General')} › ${tr('cfg_general_tab_overige',   'Overige')}`,       keywords: 'cookies tracking analytics overige misc others' },

        // ── Pages sub-tabs ──
        { id: 'pages-front',     main: 1, sub: { pagesTab: 0 }, label: tr('cfg_pages_tab_front',      'Front page'),   crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_front',      'Front page')}`,   keywords: 'front home landing voorpagina accueil' },
        { id: 'pages-404',       main: 1, sub: { pagesTab: 1 }, label: tr('cfg_pages_tab_404',        '404 page'),     crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_404',        '404 page')}`,     keywords: 'not found 404 error' },
        { id: 'pages-cart',      main: 1, sub: { pagesTab: 2 }, label: tr('cfg_pages_tab_cart',       'Cart page'),    crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_cart',       'Cart page')}`,    keywords: 'cart winkelmand panier checkout' },
        { id: 'pages-about',     main: 1, sub: { pagesTab: 3 }, label: tr('cfg_pages_tab_about',      'About'),        crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_about',      'About')}`,        keywords: 'about over ons à propos' },
        { id: 'pages-disclaim',  main: 1, sub: { pagesTab: 4 }, label: tr('cfg_pages_tab_disclaimer', 'Disclaimer'),   crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_disclaimer', 'Disclaimer')}`,   keywords: 'disclaimer legal mentions' },
        { id: 'pages-privacy',   main: 1, sub: { pagesTab: 5 }, label: tr('cfg_pages_tab_privacy',    'Privacy'),      crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_privacy',    'Privacy')}`,      keywords: 'privacy gdpr confidentialité' },
        { id: 'pages-dynamic',   main: 1, sub: { pagesTab: 6 }, label: tr('cfg_pages_tab_dynamic',    'Dynamic page'), crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_dynamic',    'Dynamic page')}`, keywords: 'dynamic dynamische pagina' },

        // ── Style sub-tabs ──
        { id: 'style-explorer',  main: 2, sub: { styleTab: 0 }, label: tr('cfg_style_tab_explorer',       'Verkenner'),       crumb: 'Style › Verkenner',       keywords: 'explorer verkenner explore listing grid' },
        { id: 'style-pdetail',   main: 2, sub: { styleTab: 1 }, label: tr('cfg_style_tab_product_detail', 'Product detail'),  crumb: 'Style › Product detail',  keywords: 'product detail fiche productdetail' },

        // ── Style → individual config modals (jump straight in) ──
        // Shared modals default to Verkenner since the edited state applies
        // to both Style sub-tabs.
        { id: 'modal-language',     main: 2, sub: { styleTab: 0 }, modal: { section: 'language',          mode: 'config' }, label: 'Language bar',                       crumb: 'Style › Language bar',                  keywords: 'language taalbalk languages flags switcher' },
        { id: 'modal-header',       main: 2, sub: { styleTab: 0 }, modal: { section: 'header',           mode: 'config' }, label: 'Header (logo)',                      crumb: 'Style › Header (logo)',                 keywords: 'header logo brand banner top' },
        { id: 'modal-menu',         main: 2, sub: { styleTab: 0 }, modal: { section: 'menu',             mode: 'config' }, label: 'Menu bar',                           crumb: 'Style › Menu bar',                      keywords: 'menu navigation nav navigatiebalk navigatie' },
        { id: 'modal-footer',       main: 2, sub: { styleTab: 0 }, modal: { section: 'footer',           mode: 'config' }, label: 'Footer',                             crumb: 'Style › Footer',                        keywords: 'footer voetbalk' },
        { id: 'modal-footer-extra', main: 2, sub: { styleTab: 0 }, modal: { section: 'footerExtra',      mode: 'config' }, label: 'Footer (extra)',                     crumb: 'Style › Footer (extra)',                keywords: 'footer extra payment methods extra onderbalk' },
        { id: 'modal-widths',       main: 2, sub: { styleTab: 0 }, modal: { section: 'globalWidths',     mode: 'config' }, label: 'Global width settings',              crumb: 'Style › Global width settings',         keywords: 'width breedte container max-width' },
        { id: 'modal-groups',       main: 2, sub: { styleTab: 0 }, modal: { section: 'groups',           mode: 'config' }, label: 'Filters and sorting',                crumb: 'Style › Verkenner › Filters and sorting',  keywords: 'filters sortering groepen filter sorting groups categories' },
        { id: 'modal-pcard',        main: 2, sub: { styleTab: 0 }, modal: { section: 'productCard',      mode: 'config' }, label: 'Product information (Verkenner)',     crumb: 'Style › Verkenner › Product information', keywords: 'product card information lines image fit aspect ratio size preview hover' },
        { id: 'modal-pdi',          main: 2, sub: { styleTab: 1 }, modal: { section: 'productDetailInfo', mode: 'config' }, label: 'Product detail information',         crumb: 'Style › Product detail › Product detail information', keywords: 'product detail information layout sections breadcrumb related stock' },
        { id: 'modal-image',        main: 2, sub: { styleTab: 1 }, modal: { section: 'image',            mode: 'config' }, label: 'Image gallery',                      crumb: 'Style › Product detail › Image gallery', keywords: 'image gallery product image thumbnail aspect arrows fullscreen' },

        // ── Processing sub-tabs ──
        { id: 'proc-general',    main: 3, sub: { procTab: 0 }, label: tr('cfg_proc_tab_general',  'General'),            crumb: 'Processing › General',          keywords: 'b2b warehouse discount cart registration tickets external stock' },
        { id: 'proc-payments',   main: 3, sub: { procTab: 1 }, label: tr('cfg_proc_tab_payments', 'Payments'),           crumb: 'Processing › Payments',         keywords: 'payment betaling paiement msp multisafepay invoice' },
        { id: 'proc-shipping',   main: 3, sub: { procTab: 2 }, label: tr('cfg_proc_tab_shipping', 'Shipping & pickup'),  crumb: 'Processing › Shipping & pickup', keywords: 'shipping verzending livraison pickup afhaling delivery' },
        { id: 'proc-fields',     main: 3, sub: { procTab: 3 }, label: tr('cfg_proc_tab_fields',   'Customer fields'),    crumb: 'Processing › Customer fields',  keywords: 'customer fields klantvelden' },

        // ── Sync sub-tabs ──
        { id: 'sync-general',    main: 4, sub: { syncTab: 0 }, label: tr('cfg_sync_tab_general', 'General'),             crumb: 'Sync-settings › General',       keywords: 'source webshop stores stock filtering' },
        { id: 'sync-field',      main: 4, sub: { syncTab: 1 }, label: tr('cfg_sync_tab_field',   'Field mapping'),       crumb: 'Sync-settings › Field mapping', keywords: 'field mapping veld category language' },
        { id: 'sync-images',     main: 4, sub: { syncTab: 2 }, label: tr('cfg_sync_tab_images',  'Images'),              crumb: 'Sync-settings › Images',        keywords: 'images afbeeldingen image processing v2' },
    ];
};

/**
 * Hook holding all search state + behaviour. Adapters (setTabValue, setXxxTab,
 * openStyleModal, notify) come from the parent screen so the search stays
 * decoupled from React-Router and the screen's own local state.
 */
export const useWebshopSearch = ({
    t, theme, notify,
    setTabValue,
    setGeneralTab, setPagesTab, setStyleTab, setProcTab, setSyncTab,
    openStyleModal,
} = {}) => {
    const [shortcutClicks, setShortcutClicks] = useState(0);
    const [searchEnabled,  setSearchEnabled]  = useState(false);
    const [searchQuery,    setSearchQuery]    = useState('');
    const [searchOpen,     setSearchOpen]     = useState(false);

    // Track the active language so the full-text index rebuilds when the
    // user switches NL/EN/FR. `t` is a stable reference per call site, so
    // we can't depend on it alone.
    const [lang, setLang] = useState(() => i18n.language || 'en');
    useEffect(() => {
        const handler = (l) => setLang(l);
        i18n.on('languageChanged', handler);
        return () => i18n.off('languageChanged', handler);
    }, []);

    // Wired into the Shortcuts tab's onClick — fires on every click, even if
    // the tab is already active (Tabs.onChange wouldn't catch that case).
    const bumpShortcutClicks = () => {
        setShortcutClicks((c) => {
            const next = c + 1;
            if (next === UNLOCK_CLICKS && !searchEnabled) {
                setSearchEnabled(true);
                if (notify) notify('Search unlocked', 4000, 'top-right', theme, 2);
            }
            return next;
        });
    };

    // Curated index: the high-value navigation targets (tabs, sub-tabs,
    // modal openers). Always shown first in results.
    const curatedIndex = useMemo(() => buildSearchIndex(t), [t]);

    // Full-text index: every `cfg_*` translation. Rebuilt when the language
    // changes. May be huge (200+ entries) — that's why we filter strictly
    // and cap visible matches.
    const fullIndex = useMemo(() => buildFullIndex(lang), [lang]);

    const searchMatches = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return [];

        const matches = (it) => {
            const haystack = `${it.label} ${it.crumb} ${it.keywords || ''}`.toLowerCase();
            return haystack.includes(q);
        };

        // Curated entries first — they map to navigation targets and tend
        // to be what the user actually wants. Cap to 4 so they don't drown
        // out the deeper translation hits.
        const curatedHits = curatedIndex.filter(matches).slice(0, 4);

        // Full-text hits next. Skip any whose label is identical to one
        // already in curatedHits (avoids duplicate "Cadeaubon" rows when
        // both the curated tab entry and the translation key match).
        const seenLabels = new Set(curatedHits.map(c => c.label));
        const fullHits = fullIndex.filter(it => !seenLabels.has(it.label) && matches(it));

        // Rank within fullHits: prefix matches (label starts with q) bubble
        // above substring matches.
        fullHits.sort((a, b) => {
            const ap = a.label.toLowerCase().startsWith(q) ? 0 : 1;
            const bp = b.label.toLowerCase().startsWith(q) ? 0 : 1;
            return ap - bp;
        });

        return [...curatedHits, ...fullHits].slice(0, 12);
    }, [searchQuery, curatedIndex, fullIndex]);

    const jumpTo = (entry) => {
        if (setTabValue) setTabValue(entry.main);
        if (entry.sub) {
            if ('generalTab' in entry.sub && setGeneralTab) setGeneralTab(entry.sub.generalTab);
            if ('pagesTab'   in entry.sub && setPagesTab)   setPagesTab(entry.sub.pagesTab);
            if ('styleTab'   in entry.sub && setStyleTab)   setStyleTab(entry.sub.styleTab);
            if ('procTab'    in entry.sub && setProcTab)    setProcTab(entry.sub.procTab);
            if ('syncTab'    in entry.sub && setSyncTab)    setSyncTab(entry.sub.syncTab);
        }
        // Tab switch flushes first, then the modal opens against the new tree.
        if (entry.modal && openStyleModal) {
            setTimeout(() => openStyleModal(entry.modal.section, entry.modal.mode), 0);
        }
        setSearchQuery('');
        setSearchOpen(false);
    };

    return {
        searchEnabled,
        searchQuery,
        searchOpen,
        searchMatches,
        setSearchQuery,
        setSearchOpen,
        bumpShortcutClicks,
        jumpTo,
    };
};

/**
 * Visual search bar — TextField + dropdown of matches. Renders nothing
 * until `search.searchEnabled` is true (the easter egg gate).
 *
 * Keyboard:
 *   ↓ / ↑    move highlight through the result list
 *   Enter    jump to the highlighted result (or the first one if none yet)
 *   Tab      same as Enter — keeps the eg. spotlight-feel; also stops the
 *            browser from tabbing past the (otherwise useless) input
 *   Esc      close the popover without navigating
 */
export const WebshopSearchBar = ({ search }) => {
    const [highlight, setHighlight] = useState(0);

    // Refs to each result row so we can scroll the active one into view
    // when the highlight moves past the visible area of the popover.
    const rowRefs = useRef([]);

    // Reset the highlight to the top whenever the result list changes
    // (new query, new matches, popover re-opened).
    useEffect(() => { setHighlight(0); }, [search?.searchQuery, search?.searchMatches?.length]);

    // Bring the highlighted row into view. `block: 'nearest'` only scrolls
    // when the row is actually outside the visible area, so the popover
    // doesn't jiggle for already-visible items.
    useEffect(() => {
        const node = rowRefs.current[highlight];
        if (node && typeof node.scrollIntoView === 'function') {
            node.scrollIntoView({ block: 'nearest' });
        }
    }, [highlight]);

    if (!search?.searchEnabled) return null;

    const { searchQuery, setSearchQuery, searchOpen, setSearchOpen, searchMatches, jumpTo } = search;
    const lastIdx = Math.max(0, searchMatches.length - 1);

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSearchOpen(true);
            setHighlight((h) => Math.min(lastIdx, h + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSearchOpen(true);
            setHighlight((h) => Math.max(0, h - 1));
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            if (searchMatches.length === 0) return;
            // Tab default would move focus away — block that so the user
            // stays in the search flow.
            e.preventDefault();
            jumpTo(searchMatches[Math.min(highlight, lastIdx)]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setSearchOpen(false);
        }
    };

    return (
        <Box sx={{ position: 'relative', flexShrink: 0, py: 1 }}>
            <TextField
                size="small"
                placeholder="Search settings…"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                onKeyDown={onKeyDown}
                onFocus={() => setSearchOpen(true)}
                // Small delay so a click on a result lands BEFORE the popover
                // closes from the blur. 150ms is long enough for the click
                // event to register, short enough to feel instant.
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon       fontSize="small" sx={{ color: Colors.sft_blue }} /></InputAdornment>,
                    endAdornment:   <InputAdornment position="end"><AutoAwesomeIcon fontSize="small" sx={{ color: Colors.sft_blue, opacity: 0.6 }} /></InputAdornment>,
                }}
                sx={{ width: 260 }}
            />
            {searchOpen && searchMatches.length > 0 && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute', top: '100%', right: 0, mt: 0.5,
                        width: 320, zIndex: 50,
                        maxHeight: 360, overflowY: 'auto',
                        borderRadius: 1.5,
                    }}
                >
                    {searchMatches.map((m, i) => {
                        const isActive = i === Math.min(highlight, lastIdx);
                        return (
                            <Box
                                key={m.id}
                                ref={(el) => { rowRefs.current[i] = el; }}
                                // mouseDown + preventDefault so the input's blur
                                // doesn't fire and close the popover before the
                                // click lands.
                                onMouseDown={(e) => { e.preventDefault(); jumpTo(m); }}
                                onMouseEnter={() => setHighlight(i)}
                                sx={{
                                    px: 1.5, py: 1,
                                    cursor: 'pointer',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:last-child': { borderBottom: 'none' },
                                    backgroundColor: isActive ? 'rgba(25,118,210,0.10)' : 'transparent',
                                    borderLeft: '3px solid',
                                    borderLeftColor: isActive ? 'primary.main' : 'transparent',
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {m.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                    {m.crumb}
                                </Typography>
                            </Box>
                        );
                    })}
                </Paper>
            )}
            {searchOpen && searchQuery.trim() && searchMatches.length === 0 && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute', top: '100%', right: 0, mt: 0.5,
                        width: 320, zIndex: 50,
                        borderRadius: 1.5,
                        px: 1.5, py: 1.25,
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        No matches for &quot;{searchQuery}&quot;
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

WebshopSearchBar.propTypes = {
    search: PropTypes.shape({
        searchEnabled:  PropTypes.bool,
        searchQuery:    PropTypes.string,
        searchOpen:     PropTypes.bool,
        searchMatches:  PropTypes.array,
        setSearchQuery: PropTypes.func,
        setSearchOpen:  PropTypes.func,
        jumpTo:         PropTypes.func,
    }),
};
